import { createLazyModuleLoader } from './lazyModuleLoader.js';
import {
  buildBooklistInitContext,
  buildBookDetailInitContext,
  buildLibraryModalsInitContext
} from './moduleInitContextBuilders.js';
import { ensureModuleWithInit } from './moduleRuntimeHelpers.js';

export function createLibraryFeatureRuntime(context = {}) {
  const {
    importWithChunkRecovery,
    state,
    elements,
    showToast,
    formatDate,
    openModal,
    closeModal,
    deleteHighlight,
    updateHighlight,
    openBookAtLocation,
    showShareHighlightModal,
    syncUnifiedSearchUi,
    showEditBooklistMenu,
    showAllBooklistsModal,
    confirmDeleteBook,
    showBookDetail,
    openBook,
    saveBooklists,
    updateShelfViewModeButton,
    updatePrimaryActionButton,
    generateBooklistId,
    sortBooksForBooklist,
    sortBooklists,
    createBooklistActionCardHtml,
    createBooklistActionListItemHtml,
    createBookCard,
    createBookListItem,
    saveCategories,
    saveCollections,
    renderBookshelf,
    renderCollectionFilters,
    updateBook,
    generateCollectionId,
    escapeHtml,
    renderCategoryFilters,
    renderBooklistView,
    renderCurrentView,
    switchView,
    showAddBooklistModal,
    getBooklistBookCount,
    updateGlobalShelfOrderForCollectionOrder,
    addBooksToCollection,
    exitSelectMode,
    ensureMusicPlayerReady,
    getReaderConstructor,
    addBook,
    getNextCustomSortOrder,
    canAccessFeature,
    getBookLimit,
    openMembershipCenter
  } = context;

  let highlightsModuleApi = null;
  let highlightsModuleLoadingPromise = null;
  let highlightsModuleEventsBound = false;
  let booklistModuleApi = null;
  let booklistModuleLoadingPromise = null;
  let bookDetailModuleApi = null;
  let bookDetailModuleLoadingPromise = null;
  let libraryModalsModuleApi = null;
  let libraryModalsModuleLoadingPromise = null;
  let fileImportModuleApi = null;
  let fileImportModuleLoadingPromise = null;

  const loadHighlightsModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./highlightsModule.js'),
    getApi: () => highlightsModuleApi,
    setApi: (mod) => {
      highlightsModuleApi = mod;
    },
    getLoadingPromise: () => highlightsModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      highlightsModuleLoadingPromise = promise;
    }
  });

  async function ensureHighlightsModuleReady() {
    const mod = await loadHighlightsModule();
    if (mod?.initHighlightsModule) {
      mod.initHighlightsModule({
        state,
        elements,
        showToast,
        formatDate,
        openModal,
        closeModal,
        deleteHighlight,
        updateHighlight,
        openBookAtLocation,
        showShareHighlightModal
      });
    }
    if (!highlightsModuleEventsBound && mod?.setupHighlightsEvents) {
      mod.setupHighlightsEvents();
      highlightsModuleEventsBound = true;
    }
    return mod;
  }

  const loadBooklistModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./booklistModule.js'),
    getApi: () => booklistModuleApi,
    setApi: (mod) => {
      booklistModuleApi = mod;
    },
    getLoadingPromise: () => booklistModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      booklistModuleLoadingPromise = promise;
    }
  });

  function ensureBooklistModuleReady() {
    return ensureModuleWithInit(loadBooklistModule, {
      isInitializedExport: 'isBooklistModuleInitialized',
      initExport: 'initBooklistModule',
      createInitContext: () => buildBooklistInitContext({
        state,
        elements,
        showToast,
        openModal,
        closeModal,
        syncUnifiedSearchUi,
        showEditBooklistMenu,
        showAllBooklistsModal,
        confirmDeleteBook,
        showBookDetail,
        openBook,
        saveBooklists,
        updateShelfViewModeButton,
        updatePrimaryActionButton,
        generateBooklistId,
        sortBooksForBooklist,
        sortBooklists,
        createBooklistActionCardHtml,
        createBooklistActionListItemHtml,
        createBookCard,
        createBookListItem
      })
    });
  }

  const loadBookDetailModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./bookDetailModule.js'),
    getApi: () => bookDetailModuleApi,
    setApi: (mod) => {
      bookDetailModuleApi = mod;
    },
    getLoadingPromise: () => bookDetailModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      bookDetailModuleLoadingPromise = promise;
    }
  });

  function ensureBookDetailModuleReady() {
    return ensureModuleWithInit(loadBookDetailModule, {
      isInitializedExport: 'isBookDetailModuleInitialized',
      initExport: 'initBookDetailModule',
      createInitContext: () => buildBookDetailInitContext({
        state,
        elements,
        showToast,
        openModal,
        closeModal,
        openBook,
        confirmDeleteBook,
        saveCategories,
        saveCollections,
        renderBookshelf,
        renderCollectionFilters,
        updateBook,
        generateCollectionId,
        escapeHtml
      })
    });
  }

  const loadLibraryModalsModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./libraryModalsModule.js'),
    getApi: () => libraryModalsModuleApi,
    setApi: (mod) => {
      libraryModalsModuleApi = mod;
    },
    getLoadingPromise: () => libraryModalsModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      libraryModalsModuleLoadingPromise = promise;
    }
  });

  function ensureLibraryModalsModuleReady() {
    return ensureModuleWithInit(loadLibraryModalsModule, {
      isInitializedExport: 'isLibraryModalsModuleInitialized',
      initExport: 'initLibraryModalsModule',
      createInitContext: () => buildLibraryModalsInitContext({
        state,
        elements,
        showToast,
        openModal,
        closeModal,
        saveCategories,
        saveCollections,
        saveBooklists,
        updateBook,
        renderCategoryFilters,
        renderCollectionFilters,
        renderBookshelf,
        renderBooklistView,
        renderCurrentView,
        switchView,
        showAddBooklistModal,
        getBooklistBookCount,
        generateCollectionId,
        updateGlobalShelfOrderForCollectionOrder,
        addBooksToCollection,
        exitSelectMode,
        escapeHtml
      })
    });
  }

  const loadFileImportModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./fileImportModule.js'),
    getApi: () => fileImportModuleApi,
    setApi: (mod) => {
      fileImportModuleApi = mod;
    },
    getLoadingPromise: () => fileImportModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      fileImportModuleLoadingPromise = promise;
    }
  });

  function ensureFileImportModuleReady() {
    return ensureModuleWithInit(loadFileImportModule, {
      isInitializedExport: 'isFileImportModuleInitialized',
      initExport: 'initFileImportModule',
      createInitContext: () => ({
        state,
        elements,
        showToast,
        ensureMusicPlayerReady,
        getReaderConstructor,
        addBook,
        updateBook,
        renderBookshelf,
        getNextCustomSortOrder,
        canAccessFeature,
        getBookLimit,
        openMembershipCenter
      })
    });
  }

  return {
    ensureHighlightsModuleReady,
    getHighlightsModuleApi: () => highlightsModuleApi,
    isHighlightsModuleEventsBound: () => highlightsModuleEventsBound,
    ensureBooklistModuleReady,
    getBooklistModuleApi: () => booklistModuleApi,
    ensureBookDetailModuleReady,
    ensureLibraryModalsModuleReady,
    ensureFileImportModuleReady
  };
}
